import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Prompt } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { v4 } from 'uuid';
import classnames from 'classnames';
import Cookies from 'universal-cookie';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import locale_en from '@fullcalendar/core/locales/en-gb';
import locale_pl from '@fullcalendar/core/locales/pl';
import locale_zh from '@fullcalendar/core/locales/zh-cn';

import moment from 'moment-timezone';
import get from 'lodash/get';
import isArray from 'lodash/isArray';

import {
  addLocalAvailability,
  updateLocalAvailability,
  deleteLocalAvailability,
  createOrUpdateAvailabilities,
  getAvailabilities,
  logSuccessfulCreateOrUpdate,
  getBookings
} from 'store/availabilities/availabilities.actions.js';
import AVAILABILITIES_ACTIONS from 'store/availabilities/availabilities.constants.js';
import { openModal, displayAlert } from 'store/v1/ui/ui.actions.js';
import { convertDateToDateTime } from 'v1/components/feature/Calendars/ResourceCalendarComponents/BigCalendar/BigCalendar';
import { decodeAndReadToken } from 'lib/auth/tokenOperations';
import { checkOverlapping } from 'v1/helpers/misc';
import titleBuilder from './TooltipTemplates.jsx';
import {
  DURATION_TYPES,
  DURATION_DEFAULTS
} from 'v1/helpers/byModel/EventHelper';
import useEvent from 'v1/helpers/hooks/useEvent';

import CalendarPens from './CalendarPens/CalendarPens';
import { RESOURCE_PORTAL } from 'features/ResourcePortal/ResourcePortalRoute';
import AvailabilityEditDialog from './AvailabilityEditDialog';
import CalendarDeletePopover from './CalendarDeletePopover/CalendarDeletePopover';
import MobileCalendarHint from './MobileCalendarHint/MobileCalendarHint';
import FullCalendarEvent from 'v1/components/feature/Calendars/CalendarEventComponents/FullCalendarEvent/FullCalendarEvent';
import {
  Grid,
  GridCell,
  Loading,
  PressStud,
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v1/components/shared';

import styles from './CalendarRoute.module.scss';
import 'styles/v1/FullCalendar.scss';
import './ResourcePortalFullCalendar.scss';
import { useTranslation } from 'react-i18next';
import { myToastService } from 'v5/services/front/ToastService/ToastService.connected';
import { SimpleTooltip } from 'v5/design-system/Tooltip';

const PREFETCH_MONTHS = 2;

const createLocalEvent = (status, event) => ({
  availabilityId: v4(),
  statusId: status.id,
  status: status.statusType,
  backgroundColor: status.colour,
  borderColor: status.colour,
  startDate: event.startDate,
  endDate: event.endDate,
  dateType: DURATION_TYPES.RECURRING_DEFAULT
});

// This creates a struct that can be displayed in the calendar, based on an availability and it's corresponding status config
const createCalendarEventFromAvailability = (availability, status = {}, t) => {
  let title = '';
  switch (availability.status) {
    case 'AVAILABLE':
      title = t('CalendarRoute.status.AVAILABLE');
      break;
    case 'UNAVAILABLE':
      title = t('CalendarRoute.status.UNAVAILABLE');
      break;
    case 'HOLD':
      title = t(`CalendarRoute.status.${availability.status}`) || 'Hold';
      break;
    default:
      title = 'Unknown';
  }

  // end date manipulation
  const endDateString = availability.endDate.split('T')[0]; // only give moment the year-month-day part of the UTC time stamp
  const endDate = moment(endDateString).add(1, 'days').format('YYYY-MM-DD'); // Offset +1 day to accomodate calendar rendering in exclusive of date
  return {
    id: availability.availabilityId,
    title: title,
    backgroundColor: status.colour,
    borderColor: status.colour,
    start: availability.startDate,
    end: endDate,
    extendedProps: {
      status: availability.status,
      statusId: availability.statusId,
      statusType: status.statusType,
      eventId: availability.eventId,
      dateType: availability.dateType,
      estimatedDailyHours: availability.estimatedDailyHours,
      estimatedDailyMinutes: availability.estimatedDailyMinutes,
      estimatedStartTime: availability.estimatedStartTime,
      estimatedEndTime: availability.estimatedEndTime,
      hasAssignments: availability.hasAssignments
    },
    allDay: true
  };
};

const convertAvailabilitiesToEvents = (availabilities, statuses, t) => {
  return availabilities.map(availability => {
    const availabilityStatusId = availability.statusId;
    const availabilityStatus = statuses.find(status => {
      return status.id === availabilityStatusId;
    });
    return createCalendarEventFromAvailability(
      availability,
      availabilityStatus,
      t
    );
  });
};

const createCalendarEventFromBookingEvent = (
  bookingEvent,
  status,
  bookingId
) => {
  // end date manipulation
  const endDateString = bookingEvent.endDate.split('T')[0]; // only give moment the year-month-day part of the UTC time stamp
  const endDate = moment(endDateString).add(1, 'days').format('YYYY-MM-DD'); // Offset +1 day to accomodate calendar rendering in exclusive of date
  const endDateOrDefault = endDate ? endDate : bookingEvent.startDate;

  return {
    bookingId,
    id: bookingEvent.eventId,
    title: status.name,
    backgroundColor: status.colour,
    borderColor: status.colour,
    start: bookingEvent.startDate,
    end: endDateOrDefault,
    extendedProps: {
      eventType: bookingEvent.eventType,
      statusType: status.statusType,
      eventId: bookingEvent.eventId,
      dateType: bookingEvent.dateType,
      estimatedDailyHours: bookingEvent.estimatedDailyHours,
      estimatedDailyMinutes: bookingEvent.estimatedDailyMinutes,
      estimatedStartTime: bookingEvent.estimatedStartTime,
      estimatedEndTime: bookingEvent.estimatedEndTime
    },
    allDay: true
  };
};

const convertBookingEventsToEvents = bookings => {
  if (bookings.length === 0) return [];

  return bookings
    .map(({ events, status, bookingId }) =>
      events
        .filter(
          event =>
            (event.startDate !== undefined || event.startDate !== null) &&
            (event.endDate !== undefined || event.endDate != null)
        )
        .map(event =>
          createCalendarEventFromBookingEvent(event, status, bookingId)
        )
    )
    .flat();
};

const CalendarRoute = ({ isMobileHintOpen = false, onCloseMobileHint }) => {
  const { t, i18n } = useTranslation('resource-portal');
  const dispatch = useDispatch();
  const { availabilityStatuses } = useSelector(state => state.public_settings);

  const {
    request,
    local,
    availabilities,
    deleted,
    createSuccess,
    creating,
    bookings
  } = useSelector(state => state.availabilities);

  const [activeStatus, setActiveStatus] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const {
    companyName,
    companyUserFirstName,
    resourceFullName,
    startTimestampUTC,
    endTimestampUTC
  } = request;
  const startDate = moment(startTimestampUTC).format('Do MMMM');
  const endDate = moment(endTimestampUTC).format('Do MMMM');

  const cookies = new Cookies();

  const loadedRange = useRef(null);
  const [pendingRanges, setPendingRanges] = useState([]);
  const [visibleRange, setVisibleRange] = useState(null);

  const isLoadingVisibleRange =
    !visibleRange ||
    pendingRanges.some(
      range => range.start < visibleRange.end && range.end > visibleRange.start
    );

  const fetchCalendarData = (start, end) => {
    const { bearerToken, token } = decodeAndReadToken(
      cookies.get(RESOURCE_PORTAL)
    );
    const { teamSchema, resourceId } = token;
    const pending = { start, end };
    setPendingRanges(ranges => [...ranges, pending]);
    Promise.all([
      dispatch(
        getAvailabilities(teamSchema, resourceId, start, end, bearerToken)
      ),
      dispatch(getBookings(teamSchema, resourceId, start, end, bearerToken))
    ]).finally(() =>
      setPendingRanges(ranges => ranges.filter(range => range !== pending))
    );
  };

  const reloadCalendarData = () => {
    if (loadedRange.current) {
      fetchCalendarData(loadedRange.current.start, loadedRange.current.end);
    }
  };

  const onDatesSet = ({ view }) => {
    const activeStart = view.activeStart.toISOString();
    const activeEnd = view.activeEnd.toISOString();
    setVisibleRange(previous =>
      previous?.start === activeStart && previous?.end === activeEnd
        ? previous
        : { start: activeStart, end: activeEnd }
    );

    const month = moment.utc(view.currentStart).startOf('month');
    const start = month
      .clone()
      .subtract(PREFETCH_MONTHS, 'months')
      .toISOString();
    const end = month
      .clone()
      .add(PREFETCH_MONTHS + 1, 'months')
      .toISOString();
    const loaded = loadedRange.current;

    if (!loaded) {
      loadedRange.current = { start, end };
      fetchCalendarData(start, end);
      return;
    }
    if (start < loaded.start) {
      fetchCalendarData(start, loaded.start);
      loaded.start = start;
    }
    if (end > loaded.end) {
      fetchCalendarData(loaded.end, end);
      loaded.end = end;
    }
  };

  useEffect(() => {
    const defaultAvailability =
      availabilityStatuses.find(status => status.name === 'Available') ||
      (isArray(availabilityStatuses) && availabilityStatuses.length > 0
        ? availabilityStatuses[0]
        : null);
    setActiveStatus(defaultAvailability);
  }, [availabilityStatuses]);

  useEffect(() => {
    const { bearerToken, token } = decodeAndReadToken(
      cookies.get(RESOURCE_PORTAL)
    );
    const { teamSchema, availabilityRequestId } = token;

    if (
      createSuccess === AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES_SUCCESS
    ) {
      reloadCalendarData();
      dispatch(
        logSuccessfulCreateOrUpdate(
          teamSchema,
          availabilityRequestId,
          bearerToken
        )
      );

      dispatch(
        openModal('SuccessFeedbackModal', {
          title: t('CalendarRoute.successShareTitle'),
          description: t('CalendarRoute.successShare', {
            companyUserFirstName,
            companyName
          }),
          actionLabel: t('CalendarRoute.gotItThanks'),
          icon: '/images/icon_colour_send.svg',
          size: 'XS'
        })
      );
    }
  }, [request, createSuccess]);

  useEffect(() => {
    const isDirty =
      local.length > 0 ||
      availabilities.find(availability => availability.dirty === true) ||
      deleted.length > 0;
    setIsDirty(isDirty);
  }, [local, availabilities, deleted]);

  useEvent([AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES], {
    onFailure: () => {
      dispatch(displayAlert('error', t('CalendarRoute.anErrorOccurred')));
      reloadCalendarData();
    }
  });

  const displayableAvailabilities = convertAvailabilitiesToEvents(
    availabilities.filter(
      availability =>
        ['AVAILABLE', 'UNAVAILABLE'].indexOf(availability.status) !== -1
    ),
    availabilityStatuses,
    t
  );

  const displayableLocalAvailabilities = convertAvailabilitiesToEvents(
    local,
    availabilityStatuses,
    t
  );

  const displayableBookings = convertBookingEventsToEvents(bookings);

  // This calendar shows a combination of system stored availabilties, bookings and locally created ones
  const allEvents = [
    ...displayableAvailabilities,
    ...displayableLocalAvailabilities,
    ...displayableBookings
  ];

  const setActivatedStatus = status => {
    setActiveStatus(prev => (prev === status ? null : status));
  };

  const onSelect = selectInfo => {
    const calendarApi = selectInfo.view.calendar;
    if (activeStatus === null) {
      calendarApi.unselect();
      return;
    }

    const tempEvent = {
      start: selectInfo.start,
      end: convertDateToDateTime(selectInfo.end, true)
    };

    const adjustedEvents = allEvents.map(event => {
      return {
        ...event,
        end: convertDateToDateTime(event.end, true)
      };
    });
    const overlapRange = checkOverlapping(tempEvent, adjustedEvents);
    if (overlapRange !== null) {
      calendarApi.unselect();
      return;
    }

    const availability = createLocalEvent(activeStatus, {
      startDate: convertDateToDateTime(selectInfo.startStr),
      endDate: convertDateToDateTime(selectInfo.endStr, true)
    });

    flushSync(() => {
      dispatch(addLocalAvailability(availability));
    });
    calendarApi.unselect();
  };

  const onEventResize = eventResizeInfo => {
    const { event } = eventResizeInfo;
    const { id, start, end } = event;

    dispatch(
      updateLocalAvailability(id, {
        startDate: convertDateToDateTime(start),
        endDate: convertDateToDateTime(end, true)
      })
    );
  };

  const onEventDrop = info => {
    const { event } = info;
    const { id, start, end } = event;

    dispatch(
      updateLocalAvailability(id, {
        startDate: convertDateToDateTime(start),
        endDate: convertDateToDateTime(end, true)
      })
    );
  };

  const onEventDidMount = info => {
    info.el.removeAttribute('tabindex');
  };

  const onSubmit = () => {
    if (
      local.length === 0 &&
      availabilities.length === 0 &&
      deleted.length === 0
    )
      return;

    const tokenFromCookie = cookies.get(RESOURCE_PORTAL);
    const { bearerToken, token } = decodeAndReadToken(tokenFromCookie);
    const { teamSchema, resourceId } = token;

    dispatch(createOrUpdateAvailabilities(teamSchema, resourceId, bearerToken));
  };

  const displayMessage = t('CalendarRoute.leaveWithoutSubmittingChanges');
  window.addEventListener('beforeunload', event => {
    if (isDirty) {
      event.preventDefault();
      event.returnValue = displayMessage;
    }
  });

  const onHandleChange = (id, durationType, updatedDuration) => {
    if (updatedDuration === undefined) return;
    const durationDetails = {
      dateType: durationType,
      ...updatedDuration
    };
    dispatch(updateLocalAvailability(id, durationDetails));
  };

  const onDeleteAvailability = id => {
    dispatch(deleteLocalAvailability(id));
  };

  const isReadOnlyEvent = event => {
    const isBookingEvent = Boolean(get(event, 'extendedProps.bookingId'));
    const statusType = event.extendedProps?.statusType;
    const isNonEditableStatus =
      statusType && statusType !== 'AVAILABLE' && statusType !== 'UNAVAILABLE';

    return isBookingEvent || isNonEditableStatus;
  };

  return (
    <section className={styles['CalendarRoute']}>
      {isDirty && <Prompt message={displayMessage} />}
      <MobileCalendarHint
        isOpen={isMobileHintOpen}
        onClose={onCloseMobileHint}
      />
      <div className="inset-L">
        <div className={styles['CalendarRoute__details']}>
          <div className={classnames(['text-16-700-black', 'stack-S'])}>
            {t('CalendarRoute.shareAvailability', {
              resourceFullName,
              companyUserFirstName,
              companyName
            })}
          </div>
          <div className="stack-L">
            <div className="text-16-600-eggplant-lighter">
              {t('CalendarRoute.availabilityBetween', {
                companyUserFirstName,
                startDate,
                endDate
              })}
            </div>
            <div className="text-16-600-eggplant-lighter">
              {t('CalendarRoute.shareInstruction')}
            </div>
          </div>
          <Grid>
            <GridCell vcenter={true}>
              <CalendarPens
                statuses={availabilityStatuses}
                setActivatedStatus={setActivatedStatus}
                activeStatus={activeStatus}
              />
            </GridCell>
            <GridCell width="auto" vcenter={true}>
              <PressStud
                label={t('CalendarRoute.submitCalendar')}
                appearance="primary"
                action={onSubmit}
                isLoading={
                  creating === AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES
                }
                isDisabled={!isDirty}
              />
            </GridCell>
          </Grid>
        </div>
      </div>
      <Grid>
        <GridCell>
          <div
            className={classnames(
              'FullCalendar',
              styles['CalendarRoute__calendar']
            )}
            aria-busy={isLoadingVisibleRange}
          >
            {isLoadingVisibleRange && (
              <Loading className={styles['CalendarRoute__loading']} />
            )}
            <FullCalendar
              locale={
                {
                  en: locale_en,
                  pl: locale_pl,
                  zh: locale_zh
                }[i18n.language]
              }
              height="auto"
              plugins={[dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
              selectable={true}
              editable={true}
              eventOverlap={false}
              fixedWeekCount={false}
              events={allEvents}
              datesSet={onDatesSet}
              timeZone="UTC"
              eventLongPressDelay={100}
              longPressDelay={100}
              selectLongPressDelay={100}
              dayMaxEventRows={1}
              windowResizeDelay={500}
              eventClassNames={['ResourcePortalCalendarEventReset']}
              buttonText={{
                today: t('CalendarRoute.today')
              }}
              select={onSelect}
              eventResize={onEventResize}
              eventDrop={onEventDrop}
              eventDidMount={onEventDidMount}
              eventClick={({ event }) => {
                if (isReadOnlyEvent(event)) {
                  myToastService.open({
                    type: 'error',
                    message: t('CalendarRoute.cannotEditModal.message')
                  });
                }
              }}
              eventContent={({ event }) => {
                const { publicId, extendedProps } = event._def;
                const hasAssignments = extendedProps?.hasAssignments;
                const isReadOnly = isReadOnlyEvent(event);

                const eventData = {
                  title: event.title,
                  borderColor: event.backgroundColor,
                  date_type: extendedProps.dateType,
                  estimated_daily_hours: extendedProps.estimatedDailyHours,
                  estimated_daily_minutes: extendedProps.estimatedDailyMinutes,
                  estimated_start_time: extendedProps.estimatedStartTime,
                  estimated_end_time: extendedProps.estimatedEndTime,
                  start_date: event.startStr,
                  end_date: event.endStr
                };

                const tooltipTitle = titleBuilder(
                  extendedProps,
                  event.title,
                  t
                );

                const calendarEventNode = (
                  <SimpleTooltip title={tooltipTitle} side="bottom">
                    <FullCalendarEvent event={eventData} />
                  </SimpleTooltip>
                );

                if (isReadOnly) {
                  return calendarEventNode;
                }

                const deleteOnly =
                  extendedProps?.statusType === 'UNAVAILABLE' ||
                  extendedProps?.status === 'HOLD';

                if (deleteOnly) {
                  return (
                    <CalendarDeletePopover
                      id={publicId}
                      trigger={calendarEventNode}
                      handleDelete={onDeleteAvailability}
                      label={t('CalendarRoute.tooltip.deleteAvailability')}
                    />
                  );
                }

                if (hasAssignments) {
                  return (
                    <Popover>
                      <PopoverTrigger asChild>
                        {calendarEventNode}
                      </PopoverTrigger>
                      <PopoverContent>
                        <div>
                          {t('CalendarRoute.availabilityAssignedCannotEdit')}
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                }

                return (
                  <AvailabilityEditDialog
                    event={event}
                    publicId={publicId}
                    dateType={extendedProps.dateType}
                    estimatedDailyHours={(
                      extendedProps.estimatedDailyHours ??
                      DURATION_DEFAULTS.ESTIMATED_DAILY_HOURS
                    ).toString()}
                    estimatedDailyMinutes={(
                      extendedProps.estimatedDailyMinutes ??
                      DURATION_DEFAULTS.ESTIMATED_DAILY_MINUTES
                    ).toString()}
                    estimatedStartTime={(
                      extendedProps.estimatedStartTime ??
                      DURATION_DEFAULTS.ESTIMATED_START_TIME
                    ).toString()}
                    estimatedEndTime={(
                      extendedProps.estimatedEndTime ??
                      DURATION_DEFAULTS.ESTIMATED_END_TIME
                    ).toString()}
                    onHandleChange={onHandleChange}
                    onDeleteAvailability={onDeleteAvailability}
                    deleteLabel={t('CalendarRoute.tooltip.deleteAvailability')}
                    tooltip={tooltipTitle}
                    renderTrigger={props => (
                      <FullCalendarEvent {...props} event={eventData} />
                    )}
                  />
                );
              }}
            />
          </div>
        </GridCell>
      </Grid>
    </section>
  );
};

export default CalendarRoute;
